/**
 * officeHtmlSanitizer — THE single cleaning pass for pasted table HTML
 * (CONTRATO v3, T-014 item B7).
 *
 * Word/Excel/Google Docs paste `text/html` that is riddled with MSO cruft:
 * inline `style=""`, `class="MsoNormal"`, `<o:p>`, `<span>` wrappers, per-cell
 * `width`, conditional comments, nested layout tables, images, etc. Feeding that
 * to TipTap either poisons the document with foreign styles or crashes the
 * table parser.
 *
 * `sanitizeTableHtml` parses the payload with the browser DOMParser, finds the
 * top-level `<table>`(s), and rebuilds a MINIMAL, clean table keeping ONLY:
 *   - cell text content (concatenated; `<br>` for in-cell line breaks)
 *   - colspan / rowspan attributes (so merges survive as native cell attrs)
 *   - header info (`<thead>` / all-`<th>` first row → `<thead><th>`)
 * Everything else (styles, classes, `<o:p>`, spans, widths) is dropped, and
 * images inside cells collapse to their `alt` text (or empty).
 *
 * Pure function, no side effects — the same output for the same input — so it
 * is trivially unit-testable in jsdom.
 */

/** Escape a value for safe insertion as HTML text content. */
const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Extract a cell's text, flattening nested markup. `<br>` and block elements
 * (`<p>`, `<div>`) become newlines; `<img>` becomes its `alt`; `<o:p>` and
 * every other wrapper contributes only its text. Whitespace (incl. &nbsp;) is
 * collapsed and the result trimmed.
 *
 * @param {Element} cell
 * @returns {string}
 */
const getCellText = (cell) => {
  const parts = [];

  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        // Text node.
        parts.push(child.nodeValue || '');
        return;
      }
      if (child.nodeType !== 1) return; // comments, etc.

      const tag = child.tagName.toLowerCase();
      if (tag === 'br') {
        parts.push('\n');
      } else if (tag === 'img') {
        parts.push(child.getAttribute('alt') || '');
      } else if (tag === 'o:p') {
        // MSO empty-paragraph marker — ignore entirely.
      } else if (tag === 'p' || tag === 'div') {
        walk(child);
        parts.push('\n');
      } else {
        walk(child);
      }
    });
  };
  walk(cell);

  return parts
    .join('')
    .replace(/ /g, ' ') // &nbsp; → space
    .replace(/[ \t]*\n[ \t]*/g, '\n') // trim around newlines
    .replace(/[ \t]+/g, ' ') // collapse runs of spaces/tabs
    .replace(/\n{2,}/g, '\n') // collapse blank lines
    .replace(/^\n+|\n+$/g, '') // drop leading/trailing newlines
    .trim();
};

/** Direct `<td>`/`<th>` children of a row (ignores nested-table cells). */
const cellsOf = (tr) =>
  Array.from(tr.children).filter((c) => /^(td|th)$/i.test(c.tagName));

/** A row is a header row when it lives in `<thead>` or is entirely `<th>`. */
const isHeaderRow = (tr) => {
  if (tr.closest('thead')) return true;
  const cells = cellsOf(tr);
  return cells.length > 0 && cells.every((c) => c.tagName.toLowerCase() === 'th');
};

/** Build one clean `<td>`/`<th>` keeping only text + colspan/rowspan. */
const buildCell = (cell, tag) => {
  const attrs = [];
  const cs = parseInt(cell.getAttribute('colspan'), 10);
  const rs = parseInt(cell.getAttribute('rowspan'), 10);
  if (cs > 1) attrs.push(` colspan="${cs}"`);
  if (rs > 1) attrs.push(` rowspan="${rs}"`);
  const text = escapeHtml(getCellText(cell)).replace(/\n/g, '<br>');
  return `<${tag}${attrs.join('')}>${text}</${tag}>`;
};

/** Rebuild a single `<table>` into clean `<table><thead?><tbody>` HTML. */
const buildTable = (table) => {
  const rows = Array.from(table.querySelectorAll('tr')).filter(
    (tr) => cellsOf(tr).length > 0
  );
  if (!rows.length) return '';

  const renderRow = (tr, asHeader) =>
    `<tr>${cellsOf(tr)
      .map((c) => buildCell(c, asHeader ? 'th' : 'td'))
      .join('')}</tr>`;

  if (isHeaderRow(rows[0])) {
    const thead = `<thead>${renderRow(rows[0], true)}</thead>`;
    const tbody = `<tbody>${rows
      .slice(1)
      .map((tr) => renderRow(tr, false))
      .join('')}</tbody>`;
    return `<table>${thead}${tbody}</table>`;
  }

  return `<table><tbody>${rows
    .map((tr) => renderRow(tr, false))
    .join('')}</tbody></table>`;
};

/**
 * Sanitize arbitrary pasted HTML down to clean `<table>` markup.
 *
 * @param {string} html - raw clipboard `text/html`
 * @returns {string|null} joined clean `<table>` HTML, or null if none present
 */
export const sanitizeTableHtml = (html) => {
  if (!html || typeof DOMParser === 'undefined') return null;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Only top-level tables — skip tables nested inside another table (MSO
    // layout wrappers), whose cell content is already captured by the parent.
    const tables = Array.from(doc.querySelectorAll('table')).filter(
      (t) => !t.parentElement || !t.parentElement.closest('table')
    );
    if (!tables.length) return null;
    const out = tables.map(buildTable).filter(Boolean).join('');
    return out || null;
  } catch (error) {
    console.warn('sanitizeTableHtml failed:', error);
    return null;
  }
};

export default sanitizeTableHtml;
